import { useState } from 'react';
import { useNotify, useRefresh } from 'react-admin';
import { Button, Stack } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { staffAction } from '../../data/dataProvider';
import { ActionDialog } from '../../components/ActionDialog';
import { useCanModerate } from '../../auth/useCanModerate';

interface ModeratePhotoButtonsProps {
  photoId: string;
  status: string;
}

/**
 * Модерация фото с подтверждением. Решение можно пересмотреть:
 * rejected → approve (откат отклонения), approved → reject.
 */
export function ModeratePhotoButtons({
  photoId,
  status,
}: ModeratePhotoButtonsProps): JSX.Element | null {
  const notify = useNotify();
  const refresh = useRefresh();
  const canModerate = useCanModerate();
  const [dialog, setDialog] = useState<'approve' | 'reject' | null>(null);

  if (!canModerate) return null;

  async function confirm(reason: string): Promise<void> {
    if (!dialog) return;
    try {
      await staffAction('PATCH', `/staff/photos/${photoId}/moderate`, {
        action: dialog,
        reason: dialog === 'reject' && reason ? reason : undefined,
      });
      notify(dialog === 'approve' ? 'Фото одобрено' : 'Фото отклонено', {
        type: 'success',
      });
      setDialog(null);
      refresh();
    } catch (e) {
      notify(`Ошибка: ${(e as Error).message}`, { type: 'error' });
    }
  }

  const isRevision = status !== 'pending';

  return (
    <Stack direction="row" spacing={1}>
      {status !== 'approved' ? (
        <Button
          size="small"
          color="success"
          startIcon={<CheckIcon />}
          onClick={(e) => {
            e.stopPropagation();
            setDialog('approve');
          }}
        >
          Одобрить
        </Button>
      ) : null}
      {status !== 'rejected' ? (
        <Button
          size="small"
          color="error"
          startIcon={<CloseIcon />}
          onClick={(e) => {
            e.stopPropagation();
            setDialog('reject');
          }}
        >
          Отклонить
        </Button>
      ) : null}
      <ActionDialog
        open={dialog === 'approve'}
        title={isRevision ? 'Пересмотреть решение и одобрить фото?' : 'Одобрить фото?'}
        description={
          isRevision
            ? 'Прежнее отклонение будет отменено, фото станет видно пользователям.'
            : 'Фото пройдёт модерацию и станет видно пользователям.'
        }
        confirmLabel="Одобрить"
        confirmColor="success"
        onConfirm={confirm}
        onClose={() => setDialog(null)}
      />
      <ActionDialog
        open={dialog === 'reject'}
        title={isRevision ? 'Пересмотреть решение и отклонить фото?' : 'Отклонить фото?'}
        description="Фото будет скрыто от пользователей. Решение можно пересмотреть позже."
        confirmLabel="Отклонить"
        confirmColor="error"
        reason={{ label: 'Причина отклонения', required: false }}
        onConfirm={confirm}
        onClose={() => setDialog(null)}
      />
    </Stack>
  );
}
